/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback } from "react";
import { BEM } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";
import { ButtonBase } from "../ButtonBase";
import { IPropColor } from "../../CommonProps";

import "./InputButton.scss";

export interface IInputButtonProps
	extends Omit<React.ComponentPropsWithoutRef<"button">, keyof IPropColor>,
		IPropColor {
	className?: string;
	selected?: boolean;
	onClick?: React.MouseEventHandler;
}

const InputButton: React.FC<IInputButtonProps> = ({
	className,
	children,
	selected,
	onClick,
	...buttonProps
}) => {
	const baseClass = BEM("edt-input-button", null, { selected });

	const handleClick = useCallback<React.MouseEventHandler>(
		(event) => {
			if (isFunction(onClick)) {
				onClick(event);
			}
		},
		[ onClick ]
	);

	if (className) {
		baseClass.add(className);
	}

	return (
		<ButtonBase className={baseClass.toClassName()} onClick={handleClick} {...buttonProps}>
			{children}
		</ButtonBase>
	);
};

InputButton.defaultProps = {
	type: "button"
};

const InputButtonMemo = React.memo(InputButton);

export { InputButtonMemo as InputButton };
